import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Drawer, Input, Spin } from "antd";
import { PaperClipOutlined, SendOutlined } from "@ant-design/icons";
import ActionCable from "actioncable";
import moment from "moment";
import { getHeaders } from "../../../interceptor/axiosInstance";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import CaseChatService, {
  CaseChatMessagePayload,
} from "../../../services/Case/caseChat.service";
import { store } from "../../../store";
import { clearCaseChatUnread } from "../../../store/actions/caseActions";
import Notification from "../Notification";
import { NotificationTypes } from "../../../enums/notificationTypes";
import "./caseChatDrawer.scss";

const { TextArea } = Input;

const MAX_SINGLE_CHAT_FILE_BYTES = 5 * 1024 * 1024;
const MAX_CASE_CHAT_TOTAL_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function totalAttachmentBytesInMessages(messages: CaseChatMessagePayload[]): number {
  return messages.reduce((sum, m) => {
    const n = m.attachment?.byte_size;
    return sum + (typeof n === "number" && !Number.isNaN(n) ? n : 0);
  }, 0);
}

function senderTypeForRole(role: UserRoleEnum): string {
  switch (role) {
    case UserRoleEnum.ADMIN:
      return "admin";
    case UserRoleEnum.DOCTOR:
      return "doctor";
    case UserRoleEnum.SCAN_CENTER:
      return "scan_center";
    default:
      return "";
  }
}

function isOwnMessage(
  m: CaseChatMessagePayload,
  role: UserRoleEnum,
  currentUserId?: number
): boolean {
  if (currentUserId == null) return false;
  return (
    m.sender.type === senderTypeForRole(role) && m.sender.id === currentUserId
  );
}

export interface CaseChatDrawerProps {
  open: boolean;
  onClose: () => void;
  caseDetailId: number | null;
  title?: string;
  userRole: UserRoleEnum;
  currentUserId?: number;
}

export default function CaseChatDrawer({
  open,
  onClose,
  caseDetailId,
  title,
  userRole,
  currentUserId,
}: CaseChatDrawerProps) {
  const [messages, setMessages] = useState<CaseChatMessagePayload[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cableRef = useRef<any>(null);
  const subscriptionRef = useRef<any>(null);
  const seenIdsRef = useRef<Set<number>>(new Set());

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!open || !caseDetailId) return;
    CaseChatService.markRead(
      caseDetailId,
      userRole,
      () => {
        store.dispatch(clearCaseChatUnread(caseDetailId));
      },
      () => {},
      () => {}
    );
  }, [open, caseDetailId, userRole]);

  useEffect(() => {
    if (!open || !caseDetailId) return;

    seenIdsRef.current = new Set();
    setLoading(true);
    CaseChatService.fetchMessages(
      caseDetailId,
      userRole,
      (list) => {
        list.forEach((m) => seenIdsRef.current.add(m.id));
        setMessages(list);
      },
      () => setMessages([]),
      () => setLoading(false)
    );
  }, [open, caseDetailId, userRole]);

  useEffect(() => {
    if (!open || !caseDetailId) return;

    const headers = getHeaders();
    if (
      !headers["access-token"] ||
      !headers["client"] ||
      !headers["uid"]
    ) {
      return;
    }

    const url = `${process.env.REACT_APP_SOCKET_URL}?access-token=${headers["access-token"]}&client=${headers["client"]}&uid=${headers["uid"]}`;
    const cable = ActionCable.createConsumer(url);
    cableRef.current = cable;

    const sub = cable.subscriptions.create(
      {
        channel: "CaseChatChannel",
        case_detail_id: caseDetailId,
      },
      {
        received: (payload: any) => {
          if (payload?.event_type !== "case_chat_message" || !payload?.message)
            return;
          const msg = payload.message as CaseChatMessagePayload;
          if (seenIdsRef.current.has(msg.id)) return;
          seenIdsRef.current.add(msg.id);
          setMessages((prev) => [...prev, msg]);
          CaseChatService.markRead(
            caseDetailId,
            userRole,
            () => {
              store.dispatch(clearCaseChatUnread(caseDetailId));
            },
            () => {},
            () => {}
          );
        },
      }
    );
    subscriptionRef.current = sub;

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (cableRef.current) {
        cableRef.current.disconnect();
        cableRef.current = null;
      }
    };
  }, [open, caseDetailId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      setPendingFile(null);
      return;
    }
    if (file.size > MAX_SINGLE_CHAT_FILE_BYTES) {
      Notification({
        message: "Each attachment must be 5 MB or smaller.",
        type: NotificationTypes.ERROR,
      });
      setPendingFile(null);
      return;
    }
    const used = totalAttachmentBytesInMessages(messages);
    if (used + file.size > MAX_CASE_CHAT_TOTAL_ATTACHMENT_BYTES) {
      Notification({
        message:
          "This case chat has a 10 MB total limit for attachments. Remove an existing attachment from the thread or choose a smaller file.",
        type: NotificationTypes.ERROR,
      });
      setPendingFile(null);
      return;
    }
    setPendingFile(file);
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!caseDetailId || sending) return;
    if (!text && !pendingFile) return;

    if (pendingFile) {
      const used = totalAttachmentBytesInMessages(messages);
      if (used + pendingFile.size > MAX_CASE_CHAT_TOTAL_ATTACHMENT_BYTES) {
        Notification({
          message:
            "This case chat has a 10 MB total limit for attachments. Remove an existing attachment from the thread or choose a smaller file.",
          type: NotificationTypes.ERROR,
        });
        return;
      }
    }

    setSending(true);
    CaseChatService.sendMessage(
      caseDetailId,
      userRole,
      text,
      pendingFile,
      (msg) => {
        setDraft("");
        setPendingFile(null);
        if (!seenIdsRef.current.has(msg.id)) {
          seenIdsRef.current.add(msg.id);
          setMessages((prev) => [...prev, msg]);
        }
      },
      (serverMessage) => {
        if (serverMessage) {
          Notification({
            message: serverMessage,
            type: NotificationTypes.ERROR,
          });
        }
      },
      () => setSending(false)
    );
  };

  return (
    <Drawer
      title={title ? `Case chat — ${title}` : "Case chat"}
      placement="right"
      width={400}
      onClose={onClose}
      visible={open}
      destroyOnClose
      className="case-chat-drawer"
    >
      <div className="case-chat-drawer__messages" ref={scrollRef}>
        {loading ? (
          <div className="text-center p-4">
            <Spin />
          </div>
        ) : (
          messages.map((m) => {
            const mine = isOwnMessage(m, userRole, currentUserId);
            return (
              <div
                key={m.id}
                className={`case-chat-drawer__bubble ${
                  mine ? "case-chat-drawer__bubble--mine" : "case-chat-drawer__bubble--other"
                }`}
              >
                <div className="case-chat-drawer__meta">
                  {mine ? "You" : m.sender.name}
                  {" · "}
                  {m.created_at
                    ? moment(m.created_at).format("DD MMM YYYY, h:mm A")
                    : ""}
                </div>
                {m.body ? <div className="case-chat-drawer__body-text">{m.body}</div> : null}
                {m.attachment ? (
                  <div className="case-chat-drawer__attachment">
                    {m.attachment.content_type.startsWith("image/") ? (
                      <a
                        href={m.attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="case-chat-drawer__attachment-image-wrap"
                      >
                        <img
                          src={m.attachment.url}
                          alt=""
                          className="case-chat-drawer__attachment-img"
                        />
                      </a>
                    ) : (
                      <a
                        href={m.attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="case-chat-drawer__attachment-link"
                      >
                        {m.attachment.filename}
                      </a>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
      <div className="case-chat-drawer__composer">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          className="case-chat-drawer__file-input"
          onChange={handleFileChange}
          aria-hidden
        />
        {pendingFile ? (
          <div className="case-chat-drawer__pending-file">
            <span className="case-chat-drawer__pending-file-name" title={pendingFile.name}>
              {pendingFile.name}
            </span>
            <button
              type="button"
              className="case-chat-drawer__pending-file-clear"
              onClick={() => setPendingFile(null)}
            >
              ×
            </button>
          </div>
        ) : null}
        <div className="case-chat-drawer__composer-row">
          <div className="case-chat-drawer__composer-main">
            <TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Type a message about this case…"
              autoSize={{ minRows: 1, maxRows: 4 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
          </div>
          <div className="case-chat-drawer__composer-actions">
            <Button
              type="text"
              icon={<PaperClipOutlined />}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach image or PDF (max 5 MB)"
              title="Attach image or PDF (max 5 MB)"
            />
            <Button
              icon={<SendOutlined />}
              loading={sending}
              onClick={handleSend}
              aria-label="Post message"
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
}
